import React, { useState, useEffect, useRef } from 'react';
import { useAPI } from '../Hooks/useAPIs';
import { useFileApi } from '../Hooks/useFileApi';
import Editor from '@monaco-editor/react';
import { XMLParser, XMLBuilder } from 'fast-xml-parser';
import { Box, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './XMLFileViewer.css';

function XMLFileViewer({ fileLocation }) {
    const { getDownloadUrl } = useFileApi();
    const { getBlobFromUrl } = useAPI();
    const muiTheme = useTheme();
    const monacoTheme = muiTheme.palette.mode === 'dark' ? 'vs-dark' : 'vs-light';
    const [xmlTextFile, setXMLTextFile] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    
    const fetchedLocationRef = useRef(null);
    const isFetchingRef = useRef(false);
    
    const options = {
        selectOnLineNumbers: true,
        lineNumbers: true,
        language: "xml",
        formatOnType: true,
        contextMenu: true,
        autoIndent: true,
        readOnly: true
    };

    useEffect(() => {
        const fetchXml = async () => {
            if (!fileLocation) {
                setIsLoading(false);
                setXMLTextFile('No file location provided');
                return;
            }

            if (fetchedLocationRef.current === fileLocation || isFetchingRef.current) {
                return;
            }

            try {
                isFetchingRef.current = true;
                setIsLoading(true);
                
                const downloadUrl = await getDownloadUrl(fileLocation);
                
                const blob = await getBlobFromUrl(downloadUrl);
                const blobText = await blob.text();
                
                const parser = new XMLParser({
                    ignoreAttributes: false,
                    attributeNamePrefix: "@_",
                    preserveOrder: true
                });
                
                const parsedXml = parser.parse(blobText);
                
                const builder = new XMLBuilder({
                    ignoreAttributes: false,
                    attributeNamePrefix: "@_",
                    format: true,
                    indentBy: "  "
                });
                
                const formattedXml = builder.build(parsedXml);
                setXMLTextFile(formattedXml);
                fetchedLocationRef.current = fileLocation;
            } catch (error) {
                console.error('Error fetching or parsing XML:', error);
                setXMLTextFile('Error loading XML content');
            } finally {
                setIsLoading(false);
                isFetchingRef.current = false;
            }
        };
        fetchXml();
    }, [fileLocation, getDownloadUrl, getBlobFromUrl]);

    if (isLoading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '200px',
                gap: 2 
            }}>
                <CircularProgress size={24} />
                <span>Loading XML content...</span>
            </Box>
        );
    }

    return (
        <div className="xml-viewer-container">
            <div className="xml-viewer-header">
                <CheckCircleIcon className="xml-viewer-header-icon" />
                <span className="xml-viewer-title">XML Viewer</span>
            </div>
            <Editor
                height="600px"
                language="xml"
                value={xmlTextFile}
                theme={monacoTheme}
                options={{
                    ...options,
                    fontSize: 14,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    wordWrap: 'on',
                }}
            />
        </div>
    );
}

export default XMLFileViewer;
